import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, Image, ActivityIndicator, FlatList } from 'react-native-web';
import Lottie from 'react-lottie';
import * as Animatable from 'react-native-animatable';
import Icon from 'react-native-vector-icons/dist/MaterialIcons';

// Custom Components
import { Button, CityPickerModal, EmptyTextSection, TierBlockView } from '../../components';

// Assets
import * as VendorChatLottie from '../../lotties/lottie_forum.json';
import * as VendorChatLoading from '../../lotties/lottie_chat_loading.json';

// Configs
import GLOBAL from '../../configs/config_global';
import { STATE_KEYS, useGlobalState } from '../../configs/config_state';
import { showAlert, showAppError } from '../../utils/util_helpers';
import { getTopicList, markAllTopicAsRead } from '../../modules/api_forum';
import { ChatItem } from '../../components/component_chat_item';
import { cloneDeep } from 'lodash';
import Popover, { PopoverMode, PopoverPlacement } from 'react-native-popover-view';
import * as RootNavigation from '../../navigation/RootNavigation';
import RouteKeys from '../../navigation/RouteKeys';
import Feather from 'react-native-vector-icons/dist/Feather';

export function Chat() {
    // region Refs
    const TOPIC_PER_PAGE = 20;
    const HAS_ACCESS = GLOBAL.DATA?.USER?.['tier'] > 0;
    const filterButton = useRef();
    const cityPicker = useRef();
    // endregion

    // region Global State
    const [globalTruckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    // endregion

    // region Local State
    const [isLoading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [lastPage, setLastPage] = useState(0);
    const [totalTopics, setTotalTopics] = useState(0);
    const [topicList, setTopicList] = useState([]);

    const [unreadCount, setUnreadCount] = useState(0);

    const [selectedCityID, setSelectedCityID] = useState(-1);

    const [showFilterPopover, setShowFilterPopover] = useState(false);
    const [fillInNeeded, setFillInNeeded] = useState(false);
    const [filterYourPosts, setFilterYourPosts] = useState(false);
    const [filterYourComments, setFilterYourComments] = useState(false);

    const [containerWidth, setContainerWidth] = useState(0);
    // endregion

    // region Derived Data
    const selectedCity = GLOBAL.CITIES[selectedCityID] || {};
    // endregion

    // region Component Mounting
    useEffect(() => {
        if (!HAS_ACCESS) return;

        const firstServingCityID = Number(globalTruckData?.city?.map((item) => item.id)[0] || 17828);
        goFetchTopic(firstServingCityID, true);

        setTimeout(() => setLoading(false), 3000);
    }, []);
    // endregion

    // region Function - Go Fetch Topic
    const goFetchTopic = (
        cityID = -1,
        isReset = false,
        fillInNeededChecked = fillInNeeded,
        filterYourPostsChecked = filterYourPosts,
        filterYourCommentsChecked = filterYourComments,
    ) => {
        if (isLoading && currentPage > 0) return;
        if ((currentPage >= lastPage) && !isReset) return;

        const mainFunc = () => {
            // Generate Filter Object
            const filterObj = {
                fillInNeededOnly: fillInNeededChecked,
                yourPosts: filterYourPostsChecked,
                yourComments: filterYourCommentsChecked
            };

            // Call API
            getTopicList(cityID, isReset ? 1 : currentPage + 1, TOPIC_PER_PAGE, filterObj)
                .then((result) => {
                    setTopicList(isReset ? result['data'] : topicList.concat(result['data']));
                    setCurrentPage(isReset ? 1 : result['currentPage']);
                    setLastPage(result['lastPage']);
                    setTotalTopics(result['totalResult']);
                    setUnreadCount(Number(result.meta['unread_topic_count'] || 0));
                })
                .catch((error) => showAppError(error))
                .finally(() => setLoading(false));
        };

        setLoading(true);
        setSelectedCityID(cityID);
        if (isReset) {
            setCurrentPage(0);
            setTotalTopics(0);
            setTopicList([]);
            setTimeout(() => mainFunc(), 1500);
        }
        else mainFunc();
    }
    // endregion

    // region Function - Mark All As Read
    const markAllAsRead = () => {
        showAlert({
            text: 'Mark all topics as read?',
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: GLOBAL.COLOR.MAIN,
                onPress: () => {
                    markAllTopicAsRead()
                        .then(() => {
                            // Mark everything as read
                            const newTopicList = topicList.map((item) => Object.assign(cloneDeep(item), { is_read: 1 }));
                            setTopicList(newTopicList);
                            setUnreadCount(0);

                            // Show alert
                            showAlert({ text: 'All topics marked as read' });
                        })
                        .catch((error) => showAppError(error));
                }
            }
        })
    }
    // endregion

    // region Function - On Topic Pressed
    const _onTopicPressed = (topicData) => {
        // Mark item as read
        if (!topicData['is_read']) {
            // Set data to state
            const topicIndex = topicList.findIndex((topic) => topic.id === topicData.id);
            if (topicIndex > -1) {
                const tempArr = cloneDeep(topicList);
                tempArr.splice(topicIndex, 1, Object.assign(cloneDeep(topicData), { is_read: 1 }));
                setTopicList(tempArr);
            }
        }

        // Open Details
        RootNavigation.navigate(
            RouteKeys.CHAT_DETAILS,
            {
                topicData,
                onTopicUpdated: (updatedTopic) => {
                    const newTopicList = topicList.map((item) => {
                        if (item.id === updatedTopic.id) {
                            return updatedTopic;
                        }
                        return item;
                    });
                    setTopicList(newTopicList);
                },
                onNewReplyPosted: (newTotalComment = 0) => {
                    const tempArr = cloneDeep(topicList);
                    const tempIndex = tempArr.findIndex((topic) => Number(topic.id) === Number(topicData.id));

                    if (tempIndex < 0) return;

                    tempArr[tempIndex]['comment_count'] = newTotalComment;

                    setTopicList(tempArr);
                },
                onReplyDeleted: (newTotalComment = 0) => {
                    const tempArr = cloneDeep(topicList);
                    const tempIndex = tempArr.findIndex((topic) => Number(topic.id) === Number(topicData.id));

                    if (tempIndex < 0) return;

                    tempArr[tempIndex]['comment_count'] = newTotalComment;

                    setTopicList(tempArr);
                },
                onCommentsRead: () => {
                    const tempArr = cloneDeep(topicList);
                    const tempIndex = tempArr.findIndex((topic) => Number(topic.id) === Number(topicData.id));

                    if (tempIndex < 0) return;

                    tempArr[tempIndex]['has_new_comments'] = 0;

                    setTopicList(tempArr);
                }
            }
        )
    }
    // endregion

    // region Function - On New Topic Pressed
    const _onNewTopicPressed = () => {
        RootNavigation.navigate(
            RouteKeys.CHAT_NEW,
            {
                onNewTopicCreated: (newTopic, postedCityIDs) => {
                    if (postedCityIDs.includes(selectedCityID)) {
                        setTopicList([newTopic].concat(topicList));
                    }
                }
            }
        )
    }
    // endregion

    // region Render Filter Button
    const renderFilterButton = () => {
        const filterCount = 0 + (fillInNeeded ? 1 : 0) + (filterYourPosts ? 1 : 0) + (filterYourComments ? 1 : 0);
        return(
            <TouchableOpacity
                ref={filterButton}
                onPress={() => setShowFilterPopover(!showFilterPopover)}
                style={{
                    flexDirection: 'row', alignItems: 'center', backgroundColor: 'white',
                    borderColor: GLOBAL.COLOR.DIVIDER, borderWidth: 0.5, borderRadius: 6,
                    paddingVertical: 12, paddingHorizontal: 16, opacity: isLoading ? 0.5 : 1
                }}>
                {/* Icon */}
                <Image
                    source={require('../../images/filter@3x.png')}
                    style={{ width: 24, height: 24 }}
                />

                {/* Text */}
                <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: '500', fontSize: 18, textAlign: 'left', marginLeft: 12 }}>
                    Filter
                </Text>

                {/* Filter Count Badge */}
                {
                    filterCount > 0 &&
                    <View style={{ paddingVertical: 4, paddingHorizontal: 8, backgroundColor: GLOBAL.COLOR.BASIC.RED, borderRadius: 20, marginLeft: 8 }}>
                        <Text style={{ color: 'white', fontSize: 13 }}>
                            {filterCount}
                        </Text>
                    </View>
                }
            </TouchableOpacity>
        );
    }
    // endregion

    // region Render Nav Bar
    const renderNavBar = () => {
        const currentItemCount = currentPage * TOPIC_PER_PAGE;
        const shouldShowCityName = HAS_ACCESS && selectedCity.name;
        return(
            <View style={{
                flexDirection: 'row', width: '100%', alignItems: 'center',
                backgroundColor: 'white', borderBottomWidth: 1, borderBottomColor: GLOBAL.COLOR.DIVIDER,
                paddingVertical: 24, paddingHorizontal: 32
            }}>
                {/* Title and Count */}
                <View style={{ flex: 1 }}>
                    {/* Title and Reload Button */}
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        {/* Title */}
                        <Text style={{ fontSize: 28, fontWeight: '900', color: 'black', marginRight: 16 }}>
                            Vendor Chat
                            {
                                shouldShowCityName &&
                                <Text style={{ fontSize: 28, fontWeight: '900', color: GLOBAL.COLOR.TEXT_GRAY, letterSpacing: 1.5 }}>
                                    {' in '}
                                </Text>
                            }
                            {
                                shouldShowCityName &&
                                <Text onPress={() => cityPicker.current?.showModal()} style={{ fontSize: 28, fontWeight: '900', color: GLOBAL.COLOR.MAIN }}>
                                    {selectedCity.name}, ({selectedCity.state?.['short_name']})
                                </Text>
                            }
                        </Text>

                        {/* Reload Button */}
                        {
                            !isLoading &&
                            <TouchableOpacity onPress={() => goFetchTopic(selectedCityID, true)}>
                                <Feather name={'refresh-cw'} color={GLOBAL.COLOR.TEXT_GRAY} size={24}/>
                            </TouchableOpacity>
                        }
                    </View>

                    {
                        HAS_ACCESS &&
                        <Text style={{ fontSize: 18, color: GLOBAL.COLOR.TEXT_GRAY, marginTop: 8 }}>
                            {
                                isLoading ?
                                    'Fetching topics...'
                                    :
                                    (
                                        totalTopics > TOPIC_PER_PAGE ?
                                            `1-${(totalTopics > currentItemCount ? currentItemCount : totalTopics)} of ${totalTopics} topic${totalTopics > 1 ? 's' : ''}`
                                            :
                                            `Total ${topicList.length} topic${topicList.length > 1 ? 's' : ''}`
                                    )
                            }
                        </Text>
                    }
                </View>

                {/* Mark All As Read */}
                {
                    (HAS_ACCESS && !isLoading && (unreadCount > 0)) &&
                    <TouchableOpacity onPress={markAllAsRead}>
                        <Text style={{ fontSize: 18, fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.UI_BLUE, marginRight: 24 }}>
                            Mark All As Read ({unreadCount})
                        </Text>
                    </TouchableOpacity>
                }

                {/* Filter Button */}
                {HAS_ACCESS && renderFilterButton()}
            </View>
        );
    }
    // endregion

    // region Render Empty Topic View
    const renderEmptyTopicView = () => {
        const isFilterOn = fillInNeeded || filterYourPosts || filterYourComments;
        return(
            <Animatable.View
                animation={'fadeIn'}
                style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center' }}>
                <EmptyTextSection
                    title={`No topic ${isFilterOn ? '' : 'yet'}`}
                    subTitle={isFilterOn ? 'Nothing was found that match your selected filters' : 'Tap the below button to create one'}
                    style={{ flex: 0 }}
                />

                {
                    !isFilterOn &&
                    <Button
                        onPress={() => {
                            if (GLOBAL.DATA.USER['tier'] === 1) {
                                showAlert({
                                    text: 'This feature is only available for Tier 2+ vendors, upgrade now using the Vendor App to enjoy all the benefits!',
                                    cancelButton: { text: 'No thanks' },
                                    confirmButton: {
                                        text: 'Show me',
                                        onPress: () => window.open('https://apps.apple.com/us/app/truckster-vendor/id1375287755', '_blank')
                                    }
                                })
                            }
                            else {
                                _onNewTopicPressed();
                            }
                        }}
                        text={'New Topic'}
                        textStyle={{ color: 'white', fontSize: 18 }}
                        style={{ alignSelf: 'center', width: 200, marginTop: 64 }}
                        image={require('../../images/iconLineEdit.png')}
                        imageNextToText={true}
                        imageColor={'white'}
                        imageSize={24}
                    />
                }
            </Animatable.View>
        )
    }
    // endregion

    // region Render Filter
    const renderCheckItem = (text = 'Filter Key', isChosen = false, onPress = () => {}) => (
        <TouchableOpacity
            style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', marginTop: 24 }}
            onPress={() => onPress()}>
            {/* Text */}
            <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 18, fontWeight: '500', flex: 1 }}>
                {text}
            </Text>

            {/* Checkbox */}
            <View style={{
                borderWidth: 2, borderRadius: 4, borderColor: isChosen ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.TEXT_GRAY,
                backgroundColor: isChosen ? GLOBAL.COLOR.MAIN : 'white', height: 32, width: 32,
                alignItems: 'center', justifyContent: 'center'
            }}>
                <Icon name={'check'} color={'white'} size={24} />
            </View>
        </TouchableOpacity>
    )

    const renderCityTag = (cityName) => (
        <View key={`cityTag_${cityName}`} style={{ paddingHorizontal: 8, paddingVertical: 6, borderRadius: 6, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, marginRight: 4, marginTop: 4 }}>
            <Text style={{ fontWeight: '500', color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 14, letterSpacing: 0.4 }}>
                {cityName}
            </Text>
        </View>
    )

    const renderFilterPopover = () => (
        <Popover
            isVisible={showFilterPopover}
            mode={PopoverMode.JS_MODAL}
            from={filterButton}
            popoverStyle={{ borderRadius: 8 }}
            placement={PopoverPlacement.BOTTOM}
            onRequestClose={() => setShowFilterPopover(false)}
            arrowShift={0}>
            <View style={{ alignItems: 'flex-start', padding: 24, width: 450 }}>
                {/* Content */}
                <Text style={{ fontSize: 30, color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: '800' }}>
                    Filters
                </Text>

                {/* Selected City */}
                <TouchableOpacity
                    onPress={() => {
                        setShowFilterPopover(false);
                        cityPicker.current?.showModal();
                    }}
                    style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', marginTop: 16 }}>
                    <Text style={{ fontWeight: '500', color: GLOBAL.COLOR.BASIC.BLACK_TWO, flex: 1 }}>
                        City
                    </Text>

                    {renderCityTag(`${selectedCity?.name}, ${selectedCity?.state?.['short_name']}`)}

                    <Text style={{ fontSize: 16, fontWeight: '500', color: GLOBAL.COLOR.BASIC.UI_BLUE, marginRight: 4, marginLeft: 12 }}>
                        More Cities
                    </Text>

                    <Icon
                        name={'keyboard-arrow-right'}
                        size={32}
                        color={GLOBAL.COLOR.BASIC.UI_BLUE}
                    />
                </TouchableOpacity>

                {/* Check Boxes */}
                {renderCheckItem('Fill-in Needed', fillInNeeded, () => {
                    setFillInNeeded(!fillInNeeded);
                    goFetchTopic(selectedCityID, true, !fillInNeeded);
                })}
                {renderCheckItem('My Posts Only', filterYourPosts, () => {
                    setFilterYourPosts(!filterYourPosts);
                    goFetchTopic(selectedCityID, true, fillInNeeded, !filterYourPosts);
                })}
                {renderCheckItem('Posts with my Comments', filterYourComments, () => {
                    setFilterYourComments(!filterYourComments);
                    goFetchTopic(selectedCityID, true, fillInNeeded, filterYourPosts, !filterYourComments);
                })}

                {/* Loading Cover */}
                {isLoading && <View style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundColor: 'rgba(255, 255, 255, 0.7)' }}/>}
            </View>
        </Popover>
    )
    // endregion

    // region Render Main Content
    const renderMainContent = () => (
        topicList.length > 0 ?
            <View
                onLayout={(event) => (containerWidth !== event.nativeEvent.layout.width) && setContainerWidth(event.nativeEvent.layout.width)}
                style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
                {/* Flat List */}
                <FlatList
                    style={{ flex: 1, width: '100%' }}
                    contentContainerStyle={{ paddingTop: 24, paddingBottom: 120, paddingHorizontal: '15%' }}
                    data={topicList}
                    keyExtractor={(item) => item.id?.toString()}
                    renderItem={({ item, index }) => (
                        <Animatable.View
                            animation={'fadeInUp'}
                            delay={(currentPage > 1 ? (index - (TOPIC_PER_PAGE * currentPage)) : index) * 150}
                            duration={600}
                            style={{ alignSelf: 'stretch' }}>
                            <TouchableOpacity
                                onPress={() => _onTopicPressed(item)}
                                style={{
                                    alignSelf: 'stretch',
                                    backgroundColor: 'white',
                                    borderRadius: 8,
                                    borderColor: GLOBAL.COLOR.DIVIDER,
                                    borderWidth: 1
                                }}>
                                <ChatItem
                                    item={item}
                                    containerWidth={containerWidth * 0.7}
                                    isInDetail={false}
                                />
                            </TouchableOpacity>
                        </Animatable.View>
                    )}
                    ItemSeparatorComponent={() => <View style={{ height: 16, width: 1 }} />}
                    removeClippedSubviews={true}
                    ListFooterComponent={() => (
                        (currentPage < lastPage) &&
                        <TouchableOpacity
                            disabled={isLoading}
                            onPress={() => goFetchTopic(selectedCityID, false)}
                            style={{
                                width: '100%', height: 64, marginTop: 24,
                                backgroundColor: GLOBAL.COLOR.MAIN,
                                alignItems: 'center', justifyContent: 'center', flexDirection: 'row',
                                borderRadius: 8, opacity: isLoading ? 0.5 : 1
                            }}>
                            {isLoading && <ActivityIndicator color={'white'} size={24} />}

                            {/* Text */}
                            <Text style={{ color: 'white', fontSize: 16, fontWeight: 'bold', marginLeft: isLoading ? 16 : 0 }}>
                                {isLoading ? 'Fetching more topics...' : `Load more topics (${totalTopics - (currentPage * TOPIC_PER_PAGE)} remaining)`}
                            </Text>
                        </TouchableOpacity>
                    )}
                />

                {/* New Topic Button */}
                <Animatable.View
                    animation={'zoomIn'}
                    delay={500}
                    duration={600}
                    style={{ position: 'absolute', bottom: 24, right: 36 }}>
                    <TouchableOpacity
                        onPress={_onNewTopicPressed}
                        style={{
                            backgroundColor: 'black', borderRadius: 12,
                            alignSelf: 'center', boxShadow: '0px 1px 5px #aeafb1, 0px -1px 5px #aeafb1',
                            flexDirection: 'row', alignItems: 'center', paddingHorizontal: 24, paddingVertical: 16
                        }}>
                        <Feather name={'plus'} size={24} color={'white'} />
                        <Text style={{ color: 'white', fontSize: 18, fontWeight: '500', marginLeft: 8 }}>
                            New Topic
                        </Text>
                    </TouchableOpacity>
                </Animatable.View>

                {/* City Picker Modal */}
                <CityPickerModal
                    ref={cityPicker}
                    title={'Select City to View Chat Topics'}
                    showDescription={true}
                    singleSelect={true}
                    visibleCityIDs={(globalTruckData?.city || []).map((city) => Number(city.id))}
                    onCitySelected={(id) => {
                        setSelectedCityID(id);
                        goFetchTopic(id, true);
                    }}
                    selectedCityIDs={selectedCity ? [selectedCityID] : []}
                />
            </View>
            :
            renderEmptyTopicView()
    )
    // endregion

    // region MAIN RENDER
    return(
        <View style={{ width: '100%', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            {/* Nav Bar */}
            {renderNavBar()}

            {/* Content */}
            <View style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
                {
                    HAS_ACCESS ?
                        (
                            (isLoading && currentPage === 0) ?
                                <View style={{ width: '100%', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                                    <Lottie
                                        options={{
                                            loop: true,
                                            autoplay: true,
                                            animationData: VendorChatLoading,
                                            rendererSettings: { preserveAspectRatio: 'xMidYMid slice' }
                                        }}
                                        width={240}
                                        height={240}
                                        isStopped={false}
                                        isPaused={false}
                                    />
                                </View>
                                :
                                renderMainContent()
                        )
                        :
                        <TierBlockView
                            style={{ backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                            lottieFile={VendorChatLottie}
                            sectionBenefit={'Reply to your customer reviews to let them know that you care about them!'}
                            availableTier={1}
                        />
                }
            </View>

            {/* Filter Popover */}
            {renderFilterPopover()}
        </View>
    );
    // endregion
}
